import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { BrandLocationConfiguration } from '@squareup/shared-types-protos/squareup/cash/local/v1/seller/cash_app_local_seller_service';
import type { Location } from '@squareup/shared-types-protos/squareup/connect/v2/resources/location';
import { useActiveLocations } from '../../hooks/useLocations.js';
import { LocationSelectModal, type FulfillmentConfigMap } from '../location-select-modal/location-select-modal.js';

export interface OrderingStatusLocationModalProps {
  readonly locationConfigurations: BrandLocationConfiguration[];
  readonly fulfillmentConfigurations: FulfillmentConfigMap;
  readonly onSubmit: (updatedConfigurations: BrandLocationConfiguration[]) => Promise<void>;
  readonly onDismiss: () => void;
  readonly onToggleTracking?: (locationId: string, isEnabling: boolean) => void;
}

export function OrderingStatusLocationModal({
  locationConfigurations, fulfillmentConfigurations, onSubmit, onDismiss, onToggleTracking,
}: OrderingStatusLocationModalProps) {
  const { t } = useTranslation();
  const { data: locationData, isPending: isLocationDataLoading } = useActiveLocations();

  const locationIdsFromConfigurations = useMemo(
    () => new Set(
      locationConfigurations
        .filter((config) => config.unitToken)
        .map((config) => config.unitToken as string),
    ),
    [locationConfigurations],
  );

  // Should only show locations that also have a configuration
  const filteredLocations = useMemo(
    () => {
      const locations = locationData?.locations || [];
      return locations.filter((loc: Location) => loc.id && locationIdsFromConfigurations.has(loc.id));
    },
    [locationData?.locations, locationIdsFromConfigurations],
  );

  const initialSelectedLocationIds = useMemo(
    () => locationConfigurations
      .filter((config) => config.unitToken && !config.orderConfiguration?.orderingPaused)
      .map((config) => config.unitToken as string),
    [locationConfigurations],
  );

  const handleSubmit = async (selectedLocationIds: string[]) => {
    const updatedConfigurations: BrandLocationConfiguration[] = locationConfigurations.map((config) => ({
      ...config,
      orderConfiguration: {
        orderingPaused: !selectedLocationIds.includes(config.unitToken || ''),
      },
    }));

    await onSubmit(updatedConfigurations);
  };

  const handleToggleTracking = (locationId: string, isSelected: boolean) => {
    if (onToggleTracking) {
      onToggleTracking(locationId, isSelected);
    }
  };

  return (
    <LocationSelectModal
      title={t('orderingStatusLocationModal.title')}
      description={t('orderingStatusLocationModal.description')}
      locations={filteredLocations}
      locationIdToFulfillmentConfigurationMap={fulfillmentConfigurations}
      isLoading={isLocationDataLoading}
      initialSelectedLocationIds={initialSelectedLocationIds}
      onDismiss={onDismiss}
      onSubmit={handleSubmit}
      onToggleTracking={handleToggleTracking}
    />
  );
}
